import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DialogActions, DialogContent, useTheme } from "@mui/material";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { AddressesText } from "../../Addresses/Addresses-types";
import { handleAddUpdateAddressFormSubmission, onAddressesUpdateError, onAddressesUpdateSuccess, } from "../../Addresses/addressesUtils";
import useToast from "../../hooks/useToast";
import { AddUpdateAddressFormDialogActions } from "./AddUpdateAddressFormDialogActions";
import { AddressFormInputs } from "./AddressFormInputs";
import { useAddUpdateAddressForm } from "./useAddUpdateAddressForm";
/**
 * * FOR CONTEXT MODALS
 * This component is used in the add update address context modal.
 * Context modals present an issue with updates, as they do not update when the form elements and submit button are not directly nested under the form provider.
 * Another version of this form is used in the add update delivery modal, which does not have this issue because it is not a context modal.
 */
export const AddUpdateAddressForm_ContextModals = ({ refetchAddresses, deliveryQuestions, address, closeModal, geoLocationCoords, formId, submitButtonText, orderInfo, flowSku, dispatch, setCheckoutFormValue, showDeliveryScheduleWarningModal, }) => {
    const theme = useTheme();
    const toast = useToast();
    const { deliveryQuestionsMap, reactHookFormMethods } = useAddUpdateAddressForm(deliveryQuestions, address);
    const { handleSubmit, reset, setError, watch, setValue: setReactHookFormValue } = reactHookFormMethods;
    /**
     * *ZIP CODE ENFORCEMENT
     * We do not currently allow the user to change the zip code on an existing address.
     * However, when delivery schedule remapping is implemented, we will need to track zip code changes
     * in order to determine if an address needs remapping.
     */
    const watchUpdateAddressZip = !!(address === null || address === void 0 ? void 0 : address.addressId) ? watch("zip") : undefined;
    /**
     * *UPDATE ZIP
     * Checks for zip changes and whether or not an address change requires remapping,
     * both of which are tied into remapping effort.
     */
    useEffect(() => {
        let mounted = true;
        const isBadZipCode = !(address === null || address === void 0 ? void 0 : address.addressId) || watchUpdateAddressZip !== address.zip;
        if (mounted && !!address && isBadZipCode) {
            reset();
            toast.push(AddressesText.SORRY_NO_CHANGE_ZIP, { variant: "error" });
        }
        return () => {
            mounted = false;
        };
    }, [watchUpdateAddressZip]);
    return (_jsx(FormProvider, Object.assign({}, reactHookFormMethods, { children: _jsxs("form", Object.assign({ id: formId, onSubmit: (e) => {
                //preventDefault and stopPropagation are necessary here to stop the form submission propagating up to parent components
                e.preventDefault();
                handleSubmit(handleAddUpdateAddressFormSubmission(deliveryQuestionsMap, onAddressesUpdateSuccess(closeModal, toast, !!address, refetchAddresses, setError, setReactHookFormValue, reset, setCheckoutFormValue), onAddressesUpdateError(toast), orderInfo, showDeliveryScheduleWarningModal, flowSku, dispatch, setCheckoutFormValue))();
                e.stopPropagation();
            }, noValidate: true, style: {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                maxWidth: "100%",
                overflow: "visible",
            } }, { children: [_jsx(DialogContent, { children: _jsx(AddressFormInputs, { deliveryQuestions: deliveryQuestions, geoLocationCoords: geoLocationCoords, address: address, deliveryQuestionsMap: deliveryQuestionsMap }) }), _jsx(DialogActions, Object.assign({ sx: {
                        borderTop: `1px solid ${theme.palette.divider}`,
                        position: "sticky",
                        bottom: "0px",
                        // Setting a bgColor prevents the underlying elements from showing through the sticky div.
                        // The zIndex is 2 because a textfield label has a zIndex of 1, which would cause it to show on top of the sticky div.
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 2,
                    } }, { children: _jsx(AddUpdateAddressFormDialogActions, { formId: formId, submitButtonText: submitButtonText }) }))] })) })));
};
